import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiGet, apiDelete, apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import OButton from "../../components/reusable/OButton";
import ODataTable from "../../components/reusable/ODataTable";
import OPagination from "../../components/reusable/OPagination";
import OSelect from "../../components/reusable/OSelect";
import { AiOutlineEye, AiOutlineEdit } from "react-icons/ai";
import { HiOutlineInboxArrowDown } from "react-icons/hi2";
import AuthContext from "context/AuthContext";

const Careers = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 0,
    totalItems: 0,
    limit: 10,
  });

  const permission = user?.permission?.find((p) => p.manager === "careers");
  const [sort, setSort] = useState({ sortKey: "createdAt", sortType: "desc" });
  const [filters, setFilters] = useState({
    keyword: "",
    status: "",
    department: "",
    employmentType: "",
  });

  const fetchCareers = async (opts = {}) => {
    try {
      setLoading(true);
      const page = opts.page || pagination.page;
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("pageSize", pagination.limit);
      if (sort?.sortKey) params.set("sortKey", sort.sortKey);
      if (sort?.sortType) params.set("sortType", sort.sortType);
      if (filters.keyword) params.set("keyword", filters.keyword);
      if (filters.status) params.set("status", filters.status);
      if (filters.department) params.set("department", filters.department);
      if (filters.employmentType)
        params.set("employmentType", filters.employmentType);

      const res = await apiGet(`${apiPath.careers}?${params.toString()}`);
      const results = res?.data?.results || {};
      const items = results?.docs || [];
      setList(items);
      setPagination({
        page: results.page || page,
        pageCount: results.totalPages || 0,
        totalItems: results.totalDocs || 0,
        limit: results.limit || pagination.limit,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCareers({ page: 1 });
  }, [sort]);

  useEffect(() => {
    fetchCareers({ page: 1 });
  }, [filters.status]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCareers({ page: 1 });
    }, 300);
    return () => clearTimeout(handler);
  }, [filters.keyword]);

  const onPageChange = ({ selected }) => {
    const nextPage = selected + 1;
    fetchCareers({ page: nextPage });
  };

  const columns = [
    { title: t("O_TITLE"), key: "title", sortable: true },
    { title: t("CAREERS_DEPARTMENT"), key: "department", sortable: true },
    { title: t("O_LOCATION"), key: "location", sortable: true },
    {
      title: t("CAREERS_EMPLOYMENT_TYPE"),
      key: "employmentType",
      sortable: true,
    },
    {
      title: t("CAREERS_APPLICATIONS"),
      key: "applicationCount",
      sortable: true,
      render: (row) => row.applicationCount ?? 0,
    },
    {
      title: t("O_STATUS"),
      key: "status",
      sortable: true,
      align: "center",
      render: (row) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            row.status === "active"
              ? "bg-green-100 text-green-800"
              : row.status === "inactive"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      title: t("O_CREATED_AT"),
      key: "createdAt",
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      title: t("O_ACTION"),
      key: "actions",
      align: "center",
      render: (row) => (
        <div className="flex gap-3 justify-center">
          {(permission?.view || user?.role === "admin") && (
            <button
              className="hover:text-blue-700"
              title={t("O_VIEW")}
              onClick={() => navigate(`/careers/view/${row._id}`)}
            >
              <AiOutlineEye size={18} />
            </button>
          )}
          {(permission?.add || user?.role === "admin") && (
            <button
              className="hover:text-indigo-700"
              title={t("O_EDIT")}
              onClick={() => navigate(`/careers/edit/${row._id}`)}
            >
              <AiOutlineEdit size={18} />
            </button>
          )}
          {(permission?.view || user?.role === "admin") && (
            <button
              className="hover:text-emerald-700"
              title={t("Applications")}
              onClick={() =>
                navigate(`/careers/applications?careerId=${row._id}`)
              }
            >
              <HiOutlineInboxArrowDown size={18} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: "", key: t("All Statuses") || "All Statuses" },
    { value: "active", key: t("active") || "active" },
    { value: "inactive", key: t("inactive") || "inactive" },
    { value: "closed", key: t("closed") || "closed" },
  ];

  return (
    <div>
      <div className="bg-white">
        <div className="px-3 py-4">
          <div className="bg-white border border-[#E9EDF9]">
            <div className="border-b border-b-[#E3E3E3] flex flex-wrap items-end gap-2 px-4 py-3">
              <div className="w-full md:w-60">
                <label className="block mb-2 text-sm font-medium text-gray-900">
                  {t("O_SEARCH")}
                </label>
                <input
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  placeholder={t("SEARCH_BY_KEYWORD")}
                  value={filters.keyword}
                  onChange={(e) =>
                    setFilters({ ...filters, keyword: e.target.value })
                  }
                />
              </div>
              <div className="w-full md:w-52">
                <OSelect
                  inputLabel={t("O_STATUS")}
                  name="status"
                  register={{
                    name: "status",
                    onChange: (e) =>
                      setFilters({ ...filters, status: e.target.value }),
                  }}
                  errors={{}}
                  options={statusOptions}
                  value={filters.status}
                  wrapperClassName="w-full"
                />
              </div>
              <div className="ml-auto">
                <OButton
                  label={t("CAREERS_ADD_NEW")}
                  onClick={() => navigate("/careers/add")}
                />
              </div>
            </div>

            <ODataTable
              data={list}
              columns={columns}
              loading={loading}
              pagination={pagination}
              onPageChange={onPageChange}
              sort={sort}
              onSortChange={setSort}
            />

            <OPagination
              pagination={pagination}
              onPageChange={onPageChange}
              page={pagination.page}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
